// src/components/Layout.tsx

import React, { useState, useEffect } from "react";
import { useAuth } from "../services/AuthContext";
import { useNavigate, Routes, Route, Navigate } from "react-router-dom";

import Loader from "./utils/loader/Loader";
import ProductList from "./productos/Main";
import Dashboard from "./dashboard/Main";
import ManageCategories from "./categorias/Main";
import ManageBrands from "./marcas/Main";
import ManageHistory from "./historico/Main";
import ManageSuppliers from "./proveedores/Main";
import ManageSucursales from "./sucursales/Main";
import Gastos from "./gastos/Main";
import Sales from "./caja/Main";
import Notifications from "./notificaciones/Main";
import Sidebar from "./utils/sidebar/Sidebar";
import NavBar from "./utils/navbar/NavBar";
import Profile from "./auth/perfil/Profile";
import Configuracion from "./auth/configuracion/Configuracion";
import MessageModal from "./utils/avisos/MessageModal";
import Confetti from "react-confetti";
// IMPORTAR LOS COMPONENTES DE CAJA
import CashRegisterManagement from "./caja/cash/CashRegisterManagement";
import CashRegisterHistory from "./caja/cash/CashRegisterHistory";

const Layout: React.FC = () => {
  const { userData, isAuthenticated, hasSubscription } = useAuth();
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [profileMenuOpen, setProfileMenuOpen] = useState(false);
  const [notificationsOpen, setNotificationsOpen] = useState(false);
  const [showWelcomeMessage, setShowWelcomeMessage] = useState(false);
  const [showConfetti, setShowConfetti] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    if (!isAuthenticated) {
      navigate("/login");
    } else if (!hasSubscription) {
      navigate("/suscripcion");
    }
  }, [isAuthenticated, hasSubscription, navigate]);

  useEffect(() => {
    const showWelcomeMessage = localStorage.getItem("showWelcomeMessage") === "true";
    const showConfetti = localStorage.getItem("showConfetti") === "true";

    if (showWelcomeMessage && showConfetti) {
      setShowWelcomeMessage(true);
      setShowConfetti(true);
      localStorage.removeItem("showWelcomeMessage");
      localStorage.removeItem("showConfetti");
    }
  }, []);

  if (!userData) return <Loader />;

  const toggleSidebar = () => setSidebarOpen(!sidebarOpen);
  const toggleProfileMenu = () => setProfileMenuOpen(!profileMenuOpen);
  const toggleNotifications = () => setNotificationsOpen(!notificationsOpen);

  const handleCloseModal = () => {
    setShowWelcomeMessage(false);
    setShowConfetti(false);
  };

  const handleLogout = () => {
    localStorage.removeItem("token");
    navigate("/login");
    window.location.reload();
  };

  const handleLinkClick = () => {
    if (window.innerWidth <= 768) {
      setSidebarOpen(false);
    }
  };

  return (
    <div className="relative bg-gray-100 h-screen overflow-hidden">
      {/* Sidebar (superpuesto) */}
      <Sidebar sidebarOpen={sidebarOpen} handleLinkClick={handleLinkClick} role={userData.role} />

      {/* Main wrapper */}
      <div className="flex flex-col h-full">
        {/* Navbar - siempre ocupa el 100% */}
        <NavBar
          toggleSidebar={toggleSidebar}
          sidebarOpen={sidebarOpen}
          toggleProfileMenu={toggleProfileMenu}
          toggleNotifications={toggleNotifications}
          profileMenuOpen={profileMenuOpen}
          notificationsOpen={notificationsOpen}
          usuario={userData.nombre}
          rol={userData.role}
          orgName={userData.organization?.name || ""}
          handleLogout={handleLogout}
        />

        {/* Main Content */}
        <main
          className={`flex-1 overflow-y-auto transition-all duration-300 ease-in-out ${
            sidebarOpen ? "lg:ml-64" : "ml-0"
          }`}
        >
          <Routes>
            <Route path="/caja-venta" element={<Sales />} />
            <Route path="/notificaciones" element={<Notifications />} />
            <Route path="/perfil" element={<Profile />} />
            <Route path="/inventario" element={<ProductList />} />
            
            {/* RUTAS DE GESTIÓN DE CAJA - Disponibles para todos los usuarios autenticados */}
            <Route path="/caja" element={<CashRegisterManagement />} />
            <Route path="/caja/historial" element={<CashRegisterHistory />} />

            {userData.role === "admin" && (
              <>
                <Route path="/" element={<Dashboard />} />
                <Route path="/configuracion" element={<Configuracion />} />           
                <Route path="/historico" element={<ManageHistory />} />
                <Route path="/categorias" element={<ManageCategories />} />
                <Route path="/marcas" element={<ManageBrands />} />
                <Route path="/proveedores" element={<ManageSuppliers />} />
                <Route path="/sucursales" element={<ManageSucursales />} />
                <Route path="/gastos" element={<Gastos />} />
              </>
            )}

            <Route path="*" element={<Navigate to={userData.role === "vendedor" ? "/caja-venta" : "/"} />} />
          </Routes>
        </main>
      </div>

      {/* Confetti y bienvenida */}
      {showConfetti && <Confetti />}
      <MessageModal
        isOpen={showWelcomeMessage}
        message="¡Ya está todo listo! Ahora puedes llevar tu contabilidad, inventario y ventas de forma organizada."
        messageType="success"
        onClose={handleCloseModal}
      />
    </div>
  );
};

export default Layout;